import React, { useState, useEffect } from 'react';
import Layout from 'Component/Layouts/layout';
import { Box, Typography, Card, CardContent, Button, IconButton, Tooltip } from '@mui/material';
import { Group, Add, Edit, Delete } from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import NewTableComponent from 'Component/NewTable';
import { TrainingRequests } from 'requests/TrainingRequests';
import rootAxios from 'requests/rootAxios';
import { useDispatch, useSelector } from 'react-redux';
import FormModal from 'Component/FormModal';
import AddTrainerForm from 'Component/MultiStepTrainingForms/TrainingForm/AddTrainerForm';
import { fetchAllTrainers, fetchSuperUserRegions } from 'thunks/trainingThunks/thunk';
import GlobalConfirmationBox from 'Component/AlertDialog/GlobalConfirmationBox';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { removeTrainer, setLimit, setPage } from 'reducer/trainersSlice';
import { toast } from 'react-toastify';

const Trainers = () => {
  const dispatch = useDispatch();

  // Get trainers data from Redux
  const {
    data: { trainers, regions, totalTrainerRecords },
    loading,
    page,
    limit,
  } = useSelector((state) => state.trainersSlice);

  // Fetch trainers on component mount
  useEffect(() => {
    const skip = (page - 1) * limit;
    dispatch(fetchAllTrainers({ skip, limit }));
    if (regions.length <= 0) {
      dispatch(fetchSuperUserRegions());
    }
  }, [dispatch, page, limit]);

  const columns = [
    {
      label: 'EmpID',
      field: 'employeeId',
      sortable: true,
      width: '120px',
      render: (row) => row.employeeId || '',
    },
    {
      label: 'Full Name',
      field: 'employeeName',
      sortable: true,
      width: '200px',
      render: (row) => row.employeeName || '',
    },
    {
      label: 'Base Location',
      field: 'trainerBaseLocation',
      sortable: true,
      width: '150px',
      render: (row) => row.trainerBaseLocation || 'N/A',
    },
    {
      label: 'Designation',
      field: 'designation',
      sortable: true,
      width: '150px',
      render: (row) => row.designation || '',
    },
    {
      label: 'Regions',
      field: 'regionIdsMapped',
      sortable: false,
      width: '250px',
      render: (row) => {
        if (!row.regionIdsMapped || row.regionIdsMapped.length === 0) {
          return 'No regions assigned';
        }
        return row.regionIdsMapped
          .map((regionId) => {
            const region = regions.find((r) => r.regionId === regionId);
            return region ? region.regionName : `ID:${regionId}`;
          })
          .join(', ');
      },
    },
    {
      label: 'Action',
      field: 'edit',
      sortable: false,
      width: '120px',
      render: (row) => (
        <FormModal
          buttonName={'Edit'}
          title={'Select Region'}
          startIcon={<EditIcon />}
          form={
            <AddTrainerForm
              employee={[
                {
                  employeeId: row.employeeId,
                  employeeName: row.employeeName,
                },
              ]}
              region={
                row.regionIdsMapped
                  ? row.regionIdsMapped
                      .map((regionId) => {
                        const region = regions.find((r) => r.regionId === regionId);
                        return region
                          ? { regionId: region.regionId, regionName: region.regionName }
                          : null;
                      })
                      .filter(Boolean)
                  : []
              }
              disabled={['employee']}
              trigger={() => {
                dispatch(fetchAllTrainers({ skip: (page - 1) * limit, limit }));
              }}
            />
          }
        />
      ),
    },
    {
      label: 'Remove',
      field: 'action',
      sortable: false,
      width: '120px',
      render: (row) => (
        <GlobalConfirmationBox
          key={row.employeeId}
          actionButtonName="Delete"
          description="Are you sure you want to remove this user as trainer?"
          startIcon={<DeleteIcon />}
          onConfirm={(row) => {
            // this is a soft delete operation.
            let tempRow = { ...row };
            row['employee'] = [
              {
                employeeId: row.employeeId,
                employeeName: row.employeeName,
              },
            ];
            delete row['employeeId'];
            delete row['employeeName'];
            TrainingRequests.updateTrainee(row)
              .then(() => {
                console.log('updating list');
                dispatch(removeTrainer(tempRow));
              })
              .catch((error) => {
                console.error('Error deleting trainer:', error);
              });
          }}
          data={{ ...row, isTrainer: false }}
        />
      ),
    },
  ];

  const handlePageChange = (newPage, newLimit) => {
    dispatch(setPage(newPage));
    dispatch(setLimit(newLimit));
  };

  const handleLimitChange = (newLimit) => {
    dispatch(setLimit(newLimit));
    dispatch(setPage(1)); // Reset to first page when changing limit
  };

  const handleDownloadCSV = async () => {
    try {
      // Fetch all trainers - use a large limit to get all records
      // First, get the total count to fetch all records
      const totalCount = totalTrainerRecords || 0;

      if (totalCount === 0) {
        toast.warning('No trainers data available to download');
        return;
      }

      // Fetch all trainers with a large limit
      const response = await rootAxios.get(`/super-user/getTrainers?skip=0&limit=${totalCount}`);

      const allTrainers = response?.data?.data?.trainers || [];

      if (!allTrainers || allTrainers.length === 0) {
        toast.warning('No trainers data available to download');
        return;
      }

      // Transform data for CSV
      const downloadData = allTrainers.map((trainer) => {
        // Convert regionIdsMapped array to semicolon-separated string
        const regions = trainer.regionIdsMapped
          ? trainer.regionIdsMapped.map((regionId) => `ID:${regionId}`).join('; ')
          : '';

        // Convert assignedBranches array to semicolon-separated string
        const branches = trainer.assignedBranches
          ? trainer.assignedBranches.map((b) => `${b.branchName} (${b.branchId})`).join('; ')
          : '';

        // Handle circleHead object
        const circleHeadName = trainer.circleHead?.name || '';
        const circleHeadEmployeeId = trainer.circleHead?.employeeId || '';
        const circleHeadState = trainer.circleHead?.state || '';

        return {
          'Trainer Name': trainer.employeeName || '',
          'Employee Code': trainer.employeeId || '',
          'Base Location': trainer.trainerBaseLocation || '',
          Designation: trainer.designation || '',
          'Assigned Regions': regions,
          'Assigned Branches': branches,
          'Circle Head Name': circleHeadName,
          'Circle Head Employee ID': circleHeadEmployeeId,
          'Circle Head State': circleHeadState,
        };
      });

      // Convert to CSV
      const headers = Object.keys(downloadData[0]);
      const csvContent = [
        headers.join(','),
        ...downloadData.map((row) =>
          headers
            .map((header) => {
              const value = row[header];
              // Escape quotes and wrap in quotes if contains comma, quotes, newline, or semicolon
              const escapedValue = String(value).replace(/"/g, '""');
              return escapedValue.includes(',') ||
                escapedValue.includes('"') ||
                escapedValue.includes('\n') ||
                escapedValue.includes(';')
                ? `"${escapedValue}"`
                : escapedValue;
            })
            .join(',')
        ),
      ].join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `trainers_${dateStr}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Trainers CSV file downloaded successfully (${allTrainers.length} records)`);
    } catch (error) {
      console.error('Error downloading trainers CSV:', error);
      toast.error('Failed to download trainers data');
    }
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  color: '#1e293b',
                  mb: 0.25,
                }}
              >
                Trainers
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Manage trainers and assign them to specific regions
              </Typography>
            </Box>
            <FormModal
              buttonName={'Add Trainer'}
              title={'Select Employee and Region'}
              form={
                <AddTrainerForm
                  region={regions}
                  trigger={() => {
                    dispatch(fetchAllTrainers());
                  }}
                />
              }
            />
          </Box>
        </Box>

        {/* Table Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Trainers List
              </Typography>
              <Tooltip title="Download all trainers as CSV">
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadCSV}
                  disabled={loading || totalTrainerRecords === 0}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 2,
                  }}
                >
                  Download CSV
                </Button>
              </Tooltip>
            </Box>
            <NewTableComponent
              tableData={trainers}
              columns={columns}
              tableName="Trainers"
              count={totalTrainerRecords}
              page={page}
              limit={limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              loading={loading}
              enableSearch={true}
              enableFilters={false}
              searchPlaceholder="Search trainers..."
              useWholeRowClicked={false}
              emptyMessage="No trainers found"
              maxHeight="600px"
            />
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
};

export default Trainers;
